// What colour a unit of material is, by `unitSlot`.
//
// Its own module because two surfaces read it now — the shelf's chips and the
// readouts' stock pill — and a second copy would be a material that is one
// colour in one place and another somewhere else.
//
// **These are board colours, not kit tokens, and that is why they are hex.** A
// material has to be the same colour in the interface as it is on a belt, so
// the stone and special ladders come straight out of the renderer's own
// `PALETTE` and the metal ladder is the four tones that match it. A theme
// token here would be a second source of truth against the scene.

import type { ResourceName } from "src/api";
import { LINE_INDEX } from "src/client/ai/render/factNames";
import { PALETTE } from "src/client/ai/render/palette";
import { FUEL, GRADE_SLOTS, METAL, STONE } from "src/shared/industry";

const GRADE_TONE = ["#9aafb8", "#c9dce4", "#d7bf7b", "#e8a077"];

/**
 * A unit slot's colour, off the board's own tables — `GRADE_TEXT`'s rule one
 * line wider.
 */
export function UNIT_TONE(slot: number): string {
  const line = Math.floor(slot / GRADE_SLOTS);
  const grade = slot % GRADE_SLOTS;
  if (line === METAL) return GRADE_TONE[grade] ?? GRADE_TONE[0];
  const table =
    line === STONE ? PALETTE.stoneGrades : line === FUEL ? PALETTE.fuelGrades : PALETTE.specialGrades;
  const value = table[grade] ?? table[0];
  return `#${value.toString(16).padStart(6, "0")}`;
}

/** A material the contract names, as the slot the tables and `units-<slot>` are keyed by. */
export const slotOf = (unit: { line: ResourceName; grade: number }): number =>
  LINE_INDEX[unit.line] * GRADE_SLOTS + unit.grade;

/** `UNIT_TONE`, for a `Holding` or a `PriceRow` rather than a slot. */
export const holdingTone = (unit: { line: ResourceName; grade: number }): string =>
  UNIT_TONE(slotOf(unit));
